import * as React from 'react'
import { createContext, PropsWithChildren, ReactElement, useContext, useMemo, useSyncExternalStore } from 'react'
import { createExchangeIdStore, ExchangeIdStore } from './exchangeIdStore'

// A store that holds nothing rather than undefined, so a component rendered outside the provider simply misses
// the cache instead of crashing the whole tree, the same trade OfficeEventHandlerContext makes (HAM-4310). It is
// deliberately stateless: a real store at module scope would be a session cache outliving the React tree.
const NO_STORE: ExchangeIdStore = {
    getRevision: () => 0,
    getImmutableId: () => undefined,
    setImmutableId: () => undefined,
    isThrottled: () => false,
    throttleUntil: () => undefined,
    subscribe: () => () => undefined,
}

const ExchangeIdContext = createContext<ExchangeIdStore>(NO_STORE)

/**
 * Holds the `translateExchangeIds` cache and the Graph back-off for the session. It is mounted above both the
 * axios interceptors, which write the back-off when Graph returns a 429, and MessageProvider, which reads both.
 */
export function ExchangeIdProvider(props: PropsWithChildren): ReactElement {
    const store = useMemo(() => createExchangeIdStore(), [])

    return <ExchangeIdContext.Provider value={store}>{props.children}</ExchangeIdContext.Provider>
}

/**
 * The store itself, for writers that are not part of a render (the axios interceptor).
 */
export function useExchangeIdStore(): ExchangeIdStore {
    return useContext(ExchangeIdContext)
}

/**
 * Subscribes to the store, so a component re-renders once an id is cached or the back-off is set. The snapshot is
 * the revision counter rather than the values themselves: those live in a Map that is mutated in place, which
 * useSyncExternalStore would either miss or loop on.
 */
export function useExchangeIds(mailbox: string | undefined): {
    getImmutableId: (itemId: string | undefined) => string | undefined
    setImmutableId: (itemId: string | undefined, immutableId: string | undefined) => void
    throttled: boolean
} {
    const store = useContext(ExchangeIdContext)
    const revision = useSyncExternalStore(store.subscribe, store.getRevision)

    return useMemo(
        () => ({
            getImmutableId: (itemId: string | undefined) => store.getImmutableId(mailbox, itemId),
            setImmutableId: (itemId: string | undefined, immutableId: string | undefined) => store.setImmutableId(mailbox, itemId, immutableId),
            throttled: store.isThrottled(mailbox),
        }),
        [store, mailbox, revision],
    )
}

export default ExchangeIdContext
